/**
 * Shared localization messages for Intelligent Recommendations (used in both role-recs and rule-recs)
 * These messages are shared across both applications and use the intelRecs.shared.* namespace
 */
const sharedInvertedMessages = {
  // Error messages
  'Data could not be loaded': 'intelRecs.shared.error.dataCouldNotBeLoaded',
  // Common UI buttons
  Export: 'intelRecs.shared.button.export',
  'Confirm Removal': 'intelRecs.shared.button.confirmRemoval',
  'Ignoring...': 'intelRecs.shared.button.ignoring',
  'View Details': 'intelRecs.shared.button.viewDetails',
  'Run Job': 'intelRecs.shared.button.runJob',
  'Load more': 'intelRecs.shared.button.loadMore',
  'Save & Activate': 'intelRecs.shared.button.saveAndActivate',

  // Common words: reusable across contexts
  NEW: 'intelRecs.shared.common.new',
  Filter: 'intelRecs.shared.common.filter',
  Back: 'intelRecs.shared.common.back',
  Save: 'intelRecs.shared.common.save',
  Clear: 'intelRecs.shared.common.clear',
  Applications: 'intelRecs.shared.common.applications',
  Owner: 'intelRecs.shared.common.owner',
  Rows: 'intelRecs.shared.common.rows',
  Columns: 'intelRecs.shared.common.columns',

  // Common field labels: used in row content and sidepanels
  User: 'intelRecs.shared.common.user',
  Username: 'intelRecs.shared.common.username',
  'Job Title': 'intelRecs.shared.common.jobTitle',
  Department: 'intelRecs.shared.common.department',
  Manager: 'intelRecs.shared.common.manager',
  'Access Status': 'intelRecs.shared.common.accessStatus',

  // Common status values
  Present: 'intelRecs.shared.common.present',
  Absent: 'intelRecs.shared.common.absent',
  // Common logical connectors
  And: 'intelRecs.shared.common.and',
  // Common section titles
  Condition: 'intelRecs.shared.common.condition',
  // Common labels
  Entitlements: 'intelRecs.shared.common.entitlements',
  // Common field labels
  Description: 'intelRecs.shared.common.description',

  // Common status/action words
  Cancel: 'intelRecs.shared.common.cancel',
  Ignore: 'intelRecs.shared.common.ignore',
  Error: 'intelRecs.shared.common.error',
  Redirecting: 'intelRecs.shared.common.redirecting',

  // Common error messages
  'Cannot delete the last entitlement':
    'intelRecs.shared.error.cannotDeleteLastEntitlement',
  'Error encountered.': 'intelRecs.shared.error.encountered',
  'Check logs and contact your administrator.':
    'intelRecs.shared.error.checkLogs',
  'Failed to load filter options: {error}':
    'intelRecs.shared.error.failedToLoadFilterOptions',
  'Unable to load KPI data': 'intelRecs.shared.error.kpiTiles.unableToLoadData',

  // Common action labels
  'Delete entitlement': 'intelRecs.shared.button.deleteEntitlement',
  'Collapse All': 'intelRecs.shared.button.collapseAll',

  // Empty state messages
  'No entitlements found.': 'intelRecs.shared.emptyState.noEntitlementsFound',
  'No entitlements match your search or filter criteria.':
    'intelRecs.shared.emptyState.noEntitlementsMatch',
  "Looks like there aren't any users to display.":
    'intelRecs.shared.emptyState.noUsersToDisplay',
  'No filters available or they failed to load.':
    'intelRecs.shared.emptyState.noFiltersAvailable',
  'Sorry, there are no results for the search query you entered.':
    'intelRecs.shared.emptyState.noSearchResults',

  // Tooltip messages
  "You don't have the permissions to ignore or the feature is turned off.":
    'intelRecs.shared.tooltip.noPermissionsToIgnore',
  "You don't have the permissions to activate or the feature is turned off.":
    'intelRecs.shared.tooltip.noPermissionsToActivate',

  // Modal messages
  'Remove Entitlement?': 'intelRecs.shared.modal.removeEntitlementTitle',
  'Are you sure you want to remove the {entitlement} entitlement?':
    'intelRecs.shared.modal.removeEntitlementSubtitle',
  'Advanced Options': 'intelRecs.shared.modal.advancedOptionsTitle',

  // Section titles
  'Access will be provided to these apps':
    'intelRecs.shared.sectionTitle.accessProvidedToTheseApps',
  'Included Entitlements': 'intelRecs.shared.sectionTitle.includedEntitlements',

  // Link text
  'More...': 'intelRecs.shared.link.more',

  // Banner titles
  'Congratulations!': 'intelRecs.shared.banner.congratulations',
  'Success!': 'intelRecs.shared.banner.success',
  'Oops!': 'intelRecs.shared.banner.oops',

  // Search placeholders
  'Search all Recommendations': 'intelRecs.shared.search.allRecommendations',
  'Search Entitlements': 'intelRecs.shared.search.entitlements',

  // Status messages
  'Activating...': 'intelRecs.shared.status.activating',
  'Loading filter options...': 'intelRecs.shared.status.loadingFilterOptions',
  'Loading filters...': 'intelRecs.shared.status.loadingFilters',

  // Metric labels: dashboard/card metrics
  'Total Results: {count}': 'intelRecs.shared.metrics.totalResults',

  // ICU plural format for included users count
  '{count, plural, one {# Included User} other {# Included Users}}':
    'intelRecs.shared.metrics.includedUsersCount',

  // ICU plural format for entitlements count
  '{count, plural, one {# Entitlement} other {# Entitlements}}':
    'intelRecs.shared.metrics.entitlementsCount',

  // ICU plural format for yearly requests count
  '{count, plural, one {# Yearly Request} other {# Yearly Requests}}':
    'intelRecs.shared.metrics.yearlyRequestsCount',

  // Zero state messages
  'The feature for this data is not set up.':
    'intelRecs.shared.zeroState.featureNotSetup',

  // KPI tiles labels
  'Pending Recommendations': 'intelRecs.shared.kpiTiles.pendingRecommendations',
  'Covers {count} assigned access':
    'intelRecs.shared.kpiTiles.coversAssignedAccess',

  // Side panel field labels
  Attributes: 'intelRecs.shared.sidePanel.attributes',
  Summary: 'intelRecs.shared.sidePanel.summary',
  'Employee Type': 'intelRecs.shared.sidePanel.employeeType',
  Country: 'intelRecs.shared.sidePanel.country',
  City: 'intelRecs.shared.sidePanel.city',
  'Cost Center': 'intelRecs.shared.sidePanel.costCenter',
  'Entitlement Type': 'intelRecs.shared.sidePanel.entitlementType',
  'Entitlement Description':
    'intelRecs.shared.sidePanel.entitlementDescription',
  'Entitlement Glossary': 'intelRecs.shared.sidePanel.entitlementGlossary',
  'Entitlement Privileged': 'intelRecs.shared.sidePanel.entitlementPrivileged',
  'Endpoint Name': 'intelRecs.shared.sidePanel.endpointName',
  'System Name': 'intelRecs.shared.sidePanel.systemName',

  // App access tiles - lowercase entitlements count (descriptive text)
  '{count, plural, one {# entitlement} other {# entitlements}}':
    'intelRecs.shared.appAccessTiles.entitlementsCount',
} as const;

export type SharedMessage = keyof typeof sharedInvertedMessages;

export default sharedInvertedMessages;
